import { useRef, useState, useEffect, useCallback } from 'react';

export const useCarouselScroll = () => {
  const rowRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isScrollInitialized, setIsScrollInitialized] = useState(false);

  const checkScrollState = useCallback(() => {
    const row = rowRef.current;
    if (!row) return;

    const { scrollLeft, scrollWidth, clientWidth } = row;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);

    // Enable transitions after initial check to prevent flicker
    if (!isScrollInitialized) {
      requestAnimationFrame(() => {
        setIsScrollInitialized(true);
      });
    }
  }, [isScrollInitialized]);

  const scrollToDirection = useCallback(
    (direction: 'left' | 'right') => (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const row = rowRef.current;
      if (!row) return;

      const cardWidth = row.firstElementChild?.clientWidth || 0;

      const computedStyle = window.getComputedStyle(row);
      const gapValue = computedStyle.gap || computedStyle.columnGap || '0';
      const gap = parseInt(gapValue, 10) || 0;

      const scrollAmount = (cardWidth + gap) * 2;

      row.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    },
    [],
  );

  useEffect(() => {
    const row = rowRef.current;
    if (!row) return;

    row.addEventListener('scroll', checkScrollState);
    window.addEventListener('resize', checkScrollState);
    checkScrollState();

    // eslint-disable-next-line consistent-return
    return () => {
      row.removeEventListener('scroll', checkScrollState);
      window.removeEventListener('resize', checkScrollState);
    };
  }, [checkScrollState]);

  return {
    rowRef,
    canScrollLeft,
    canScrollRight,
    scrollToDirection,
    checkScrollState,
    isScrollInitialized,
  };
};
